import { useCallback, useEffect, useRef, useState } from "react";
import { makeDefaultDeps } from "../commands/deps.js";
import { loadSharedForecasts } from "../commands/loadSharedForecasts.js";

// Command deps: the hook takes no dispatchRun/runStateRef args (the ratchet's
// hooksTakingRunArgs counter); the command dispatches into the store and AppInner's
// `useRunStore((s) => s)` subscription re-renders.
const cmdDeps = makeDefaultDeps();

/**
 * Gates whether `loadMore()` may attempt a fetch. The failure branch sets `hasMore:false`
 * alongside `error:true`, so `error` must also open the gate to allow a UI retry. `inFlight`
 * always blocks re-entry, so a retry cannot race a running request.
 *
 * @param {{inFlight: boolean, hasMore: boolean, error: boolean}} args
 * @returns {boolean}
 */
export function canLoadMore({ inFlight, hasMore, error }) {
  return !inFlight && (hasMore || error);
}

/**
 * Owns the shared-with-me lane's load lifecycle only. The rows live in the store's
 * `sharedRunsById` slice, read via `selectSharedForecasts(runState)`. The hook's state stays
 * local rather than lifted into `AppInner` (the ratchet's `appInnerUseState` counter).
 *
 * `enabled` gates the initial load and defaults to `true` because the endpoint is flag-aware
 * server-side: `chatboard-org-sharing` off collapses every viewer's subjects to `[]`.
 *
 * `onPinned` receives each page's watchlist membership for the viewer. It is a callback
 * because the pin map is App's `useState`.
 *
 * MERGE, NEVER REPLACE: the caller's merge must not clobber the owner leg's map (both lanes
 * feed the one map), and must let an OPTIMISTIC pin win over a page fetched before it, using
 * the same `{...page, ...prev}` order `bootForecasts` uses.
 *
 * @param {{enabled?: boolean, onPinned?: (pinned: Record<string, unknown>) => void}} [opts]
 * @returns {{loading: boolean, hasMore: boolean, error: boolean,
 *            loadMore: () => Promise<void>, reload: () => Promise<boolean>,
 *            settled: boolean}}
 */
export function useSharedForecasts({ enabled = true, onPinned } = {}) {
  // A ref so the callback's identity never re-runs the mount effect or re-creates
  // loadMore — the same reason cursorRef is a ref.
  const onPinnedRef = useRef(onPinned);
  onPinnedRef.current = onPinned;
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(false);
  // Differs from `!loading`, which is also true before the mount effect has fired. It exists
  // for the one-shot ?forecast= deep link, which needs a viewer's lane filled before it
  // resolves. A failed load settles too, so the link fails closed instead of retrying forever.
  const [settled, setSettled] = useState(false);
  // The opaque next_page_token — a ref, not state: it drives the NEXT fetch's
  // request param, never rendered, so it doesn't need to trigger a re-render
  // on its own (loading/hasMore/error already do that when they change).
  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);
  // Pages of ONE drain share a generation: `loadMore` never supersedes an earlier page.
  const genRef = useRef(0);
  // Unmount cancels through this flag, not a generation bump: StrictMode's dev-only
  // unmount/remount would otherwise strand the mount load, which never re-fires.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // While a request is out it owns the lane (the in-flight slot and `loading`), and only the
  // load of the CURRENT generation may apply its result. A superseded load (`stale`) applies
  // nothing, not even `loading`, because the newer load owns all of it.
  const runLoad = useCallback(async ({ cursor, supersede }) => {
    if (supersede) genRef.current += 1;
    const gen = genRef.current;
    inFlightRef.current = true;
    setLoading(true);
    const res = await loadSharedForecasts(cmdDeps, {
      cursor,
      cancelled: () => genRef.current !== gen || !mountedRef.current,
    });
    if (res.stale) return res;
    inFlightRef.current = false;
    setLoading(false);
    if (!res.ok) {
      // Fail closed on pagination, not on what's already loaded: stop
      // offering "load more" rather than retry-looping, but never clear
      // rows already hydrated into the store — a transient failure must not
      // look like "you have nothing shared with you".
      setError(true);
      setHasMore(false);
      return res;
    }
    setError(false);
    cursorRef.current = res.nextPageToken;
    setHasMore(res.nextPageToken != null);
    onPinnedRef.current?.(res.pinned);
    return res;
  }, []);

  const loadMore = useCallback(async () => {
    // inFlightRef, not `loading` state, guards re-entrancy: a second click before the
    // first's state update has committed must not fire a second request racing the first's
    // cursor update.
    if (!canLoadMore({ inFlight: inFlightRef.current, hasMore, error })) return;
    await runLoad({ cursor: cursorRef.current, supersede: false });
  }, [hasMore, error, runLoad]);

  // Deliberately keyed on [enabled] only: loadMore's closure is correct at first fire, and
  // re-running the effect whenever loadMore's identity changes would defeat firedRef.
  const firedRef = useRef(false);
  useEffect(() => {
    if (!enabled || firedRef.current) return;
    firedRef.current = true;
    loadMore().finally(() => setSettled(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Restarts from page one, unlike `loadMore`, which continues a cursor: a freshly-claimed
  // link share sorts by received-at, so it belongs on page one. Re-fetching page one is
  // idempotent because hydrated rows merge into the store rather than replacing it.
  const reload = useCallback(async () => {
    cursorRef.current = null;
    setError(false);
    setHasMore(true);
    const res = await runLoad({ cursor: null, supersede: true });
    // A superseded reload is not a failure: the newer one owns the lane.
    return res.stale ? true : res.ok;
  }, [runLoad]);

  return { loading, hasMore, error, loadMore, reload, settled: settled || !enabled };
}
